import { Task } from "@lit/task";
import {
    Err,
    formatDate,
    formatNumber,
    LICENSE_TYPE_LABEL,
    LicenseType,
    type Position,
    Role,
    type SeatChangeRequest,
    SeatChangeType,
} from "@pentacode/core";
import type { IconName } from "@pentacode/ui/components";
import { columnDef, verticalHeader } from "@pentacode/ui/components";
import { css, html, LitElement, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { popover } from "../../directives/popover";
import { alert, confirm } from "../../elements/alert-dialog";
import "../../elements/employee-cell";
import type { EmployeeCellInput } from "../../elements/employee-cell";
import "../../elements/scroller";
import type { AcceptVoiceControlAiConditionsDialog } from "../../elements/terms/accept-voice-control-ai-conditions-dialog";
import { app } from "../../init";
import { getSingleton } from "../../lib/singleton";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import {
    computeSeatCounts,
    diffSnapshot,
    nextEffective,
    type SeatCount,
    type SeatSnapshot,
    tierFromValidity,
    type TierState,
} from "./seat-machine";

type SeatRow = EmployeeCellInput & { id: number; positions: Position[]; readonly: boolean };

@customElement("ptc-settings-ai-assistant")
export class SettingsAiAssistant extends Routing(StateMixin(LitElement)) {
    routePattern = /settings\/ai-assistant/;

    get routeTitle() {
        return "AI-Assistent | Einstellungen";
    }

    get helpPage(): string {
        return "handbuch/einstellungen/rechnungsstellung/"; // TODO: Add help page
    }

    @state()
    private _loading = false;

    @state()
    private _avvAccepted: boolean | null = null;

    @state()
    private _saving = false;

    @state()
    private _effectiveSnapshots = new Map<number, SeatSnapshot>();

    private _seatStateTask = new Task(this, {
        task: () => app.api.getLicenseSeatState({}),
        args: () => [],
    });

    protected async handleRoute() {
        void this._loadAvvState();
    }

    private async _loadAvvState() {
        if (this._loading) {
            return;
        }
        this._loading = true;
        try {
            const state = await app.api.getVoiceControlAiConditionsState();
            this._avvAccepted = state.accepted;
        } catch (error) {
            void alert(error instanceof Err ? error.message : "Fehler beim Laden der Daten");
        }
        this._loading = false;
    }

    private async _acceptAvv() {
        const dialog = getSingleton(
            "ptc-accept-voice-control-ai-conditions-dialog"
        ) as AcceptVoiceControlAiConditionsDialog;

        const accepted = await dialog.show();

        if (accepted) {
            this._loading = true;
            try {
                await app.api.acceptVoiceControlAiConditions();
                this._avvAccepted = true;
            } catch (error) {
                void alert(error instanceof Err ? error.message : "Fehler beim Akzeptieren des Verarbeitungsvertrags");
            }
            this._loading = false;
        }
    }

    private _findSeat(employeeId: number, licenseType: LicenseType) {
        return this._seatStateTask.value?.seats.find(
            (s) => s.employeeId === employeeId && s.licenseType === licenseType
        );
    }

    private _dtoSnapshot(employeeId: number): SeatSnapshot {
        return {
            lite: tierFromValidity(this._findSeat(employeeId, LicenseType.aiAssistantLite)),
            pro: tierFromValidity(this._findSeat(employeeId, LicenseType.aiAssistantPro)),
        };
    }

    private _effectiveSnapshot(employeeId: number): SeatSnapshot {
        return this._effectiveSnapshots.get(employeeId) ?? this._dtoSnapshot(employeeId);
    }

    private _tierOf(snapshot: SeatSnapshot, licenseType: LicenseType): TierState {
        return licenseType === LicenseType.aiAssistantLite ? snapshot.lite : snapshot.pro;
    }

    private _changes(): SeatChangeRequest[] {
        return [...this._effectiveSnapshots.entries()].flatMap(([employeeId, snapshot]) =>
            diffSnapshot(employeeId, this._dtoSnapshot(employeeId), snapshot)
        );
    }

    private _toggleSeat(employeeId: number, licenseType: LicenseType) {
        const baseline = this._dtoSnapshot(employeeId);
        const current = this._effectiveSnapshot(employeeId);
        const after = nextEffective(baseline, current, licenseType);
        const map = new Map(this._effectiveSnapshots);
        if (after.lite === baseline.lite && after.pro === baseline.pro) {
            map.delete(employeeId);
        } else {
            map.set(employeeId, after);
        }
        this._effectiveSnapshots = map;
    }

    private _discardChanges() {
        this._effectiveSnapshots = new Map();
    }

    private async _saveChanges(
        bookedSeats: Record<LicenseType, number>,
        nextBillingSeats: Record<LicenseType, number>
    ) {
        const changes = this._changes();
        if (changes.length === 0 || this._saving) return;

        if (changes.some((c) => c.type === SeatChangeType.AssignSeat)) {
            const pricePerSeat = this._seatStateTask.value?.pricePerSeat;
            if (!pricePerSeat) return;
            const confirmed = await this._openConfirmationDialog(nextBillingSeats, pricePerSeat);
            if (!confirmed) return;
        }

        try {
            this._saving = true;
            await app.api.updateLicenseSeatState({ changes, bookedSeats });
            await this._seatStateTask.run();
            this._effectiveSnapshots = new Map();
        } catch (error) {
            void alert(error instanceof Err ? error.message : "Fehler beim Speichern der Änderungen");
        } finally {
            this._saving = false;
        }
    }

    private _getSeatIcon(
        employeeId: number,
        licenseType: LicenseType
    ): { icon: IconName; color: string; tooltip: string } | null {
        const tier = this._tierOf(this._effectiveSnapshot(employeeId), licenseType);
        switch (tier) {
            case "unassigned":
                return null;
            case "assigned":
                return { icon: "icon.status.all-clear", color: "var(--color-positive)", tooltip: "Zugang aktiv" };
            case "expiring": {
                // For pending cancellations the seat record may not yet carry a
                // `validUntil`; the backend will set it to the next billing
                // cycle boundary, which we already have on the state response.
                const date = this._findSeat(employeeId, licenseType)?.validUntil ?? this._nextBillingCycle();
                return {
                    icon: "icon.object.duration",
                    color: "var(--orange)",
                    tooltip: date
                        ? `Zugang läuft am ${formatDate(date)} ab`
                        : "Zugang läuft zum Ende der Abrechnungsperiode ab",
                };
            }
            case "scheduled": {
                const date = this._findSeat(employeeId, licenseType)?.validFrom ?? this._nextBillingCycle();
                return {
                    icon: "icon.object.date",
                    color: "var(--orange)",
                    tooltip: date ? `Zugang ab ${formatDate(date)}` : "Zugang ab nächster Abrechnungsperiode",
                };
            }
        }
    }

    private _nextBillingCycle(): Date | undefined {
        return this._seatStateTask.value?.nextBillingCycle;
    }

    private async _openConfirmationDialog(
        futureNextSeats: Record<LicenseType, number>,
        pricePerSeat: Record<LicenseType, number>
    ) {
        const tiers = [LicenseType.aiAssistantLite, LicenseType.aiAssistantPro] as const;
        const nextMonthTotal = tiers.reduce((sum, tier) => sum + futureNextSeats[tier] * pricePerSeat[tier], 0);

        const rows = tiers
            .filter((tier) => futureNextSeats[tier] > 0)
            .map((tier) => {
                const count = futureNextSeats[tier];
                const subtotal = count * pricePerSeat[tier];
                return html`
                    <div class="horizontal center-aligning layout">
                        <div class="stretch">
                            ${count}x ${LICENSE_TYPE_LABEL[tier]} (${formatNumber(pricePerSeat[tier])} €)
                        </div>
                        <div>${formatNumber(subtotal)} €</div>
                    </div>
                `;
            });

        return confirm(
            html`
                <div class="horizontal start-aligning layout">
                    <ptc-ui-icon name="icon.status.warning" class="enormous"></ptc-ui-icon>
                    <div class="stretch horizontally-margined">
                        <div class="large semibold">Geänderte Kosten</div>
                        <div class="vertically-margined padded">
                            <div class="vertical spacing layout">
                                <span> Eine Änderung der Lizenzen führt zu geänderten monatlichen Kosten. </span>
                                ${rows}
                                <hr
                                    style="border: 0; border-top: 1px solid; opacity: 0.3; margin: 0 0 0.5em; width: 100%;"
                                />
                                <div class="horizontal center-aligning layout">
                                    <div class="stretch semibold">Gesamtkosten ab Folgemonat</div>
                                    <div class="semibold">${formatNumber(nextMonthTotal)} €</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `,
            "Bestätigen",
            "Abbrechen",
            { hideIcon: true, maxWidth: "30em", optionsLayout: "horizontal" }
        );
    }

    static styles = [
        shared,
        css`
            figure {
                display: grid;
                gap: 0.5em;
            }

            .save-buttons {
                display: flex;
                flex-direction: row;
                gap: 1em;

                & button {
                    min-width: 15em;
                }
            }

            ptc-ui-table {
                --ptc-ui-table-header-row-height: 7em;

                &::part(table) {
                    /* Pin Yna columns to their declared width and let the
                       employee column claim the remaining horizontal space. */
                    table-layout: fixed;
                    width: 100%;
                }

                &::part(seat-toggle) {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0.25em 0.5em;
                }
            }
        `,
    ];

    render() {
        if (!app.company) {
            return nothing;
        }

        const seatCounts = computeSeatCounts(this._seatStateTask.value?.seats ?? [], this._effectiveSnapshots);
        const bookedSeats: Record<LicenseType, number> = {
            [LicenseType.aiAssistantLite]: seatCounts[LicenseType.aiAssistantLite].futureCurrent,
            [LicenseType.aiAssistantPro]: seatCounts[LicenseType.aiAssistantPro].futureCurrent,
        };
        const nextBillingSeats: Record<LicenseType, number> = {
            [LicenseType.aiAssistantLite]: seatCounts[LicenseType.aiAssistantLite].futureNext,
            [LicenseType.aiAssistantPro]: seatCounts[LicenseType.aiAssistantPro].futureNext,
        };
        const hasPending = this._effectiveSnapshots.size > 0;

        const managers = app.company.employees.filter((e) => e.role === Role.Manager || e.role === Role.Owner);
        // We want to display all employees that can have a seat
        const seatRowsById = new Map<number, SeatRow>(
            managers.map((e) => [
                e.id,
                {
                    id: e.id,
                    firstName: e.firstName,
                    lastName: e.lastName,
                    avatar: e.avatar,
                    status: e.status,
                    staffNumber: e.staffNumber,
                    role: e.role,
                    access: e.access,
                    positions: e.positions,
                    readonly: false,
                },
            ])
        );

        // Add employees that can't book a seat, but still have one
        for (const seat of this._seatStateTask.value?.seats ?? []) {
            if (seatRowsById.has(seat.employeeId)) continue;
            seatRowsById.set(seat.employeeId, {
                id: seat.employeeId,
                firstName: seat.employeeFirstName,
                lastName: seat.employeeLastName,
                avatar: "",
                status: seat.employeeStatus,
                staffNumber: null,
                role: Role.Worker,
                access: null,
                positions: [],
                readonly: true,
            });
        }

        const seatRows = [...seatRowsById.values()].sort((a, b) => a.lastName.localeCompare(b.lastName));

        return html`
            <div class="fullbleed vertical layout">
                <ptc-scroller class="stretch">
                    <div class="double-padded narrow-content">
                        <div class="padded">
                            <h2 class="grey">Freigabe für Yna</h2>
                            ${this._avvAccepted === null
                                ? html`
                                      <div class="padded center-aligning center-justifying horizontal layout">
                                          <ptc-ui-spinner active></ptc-ui-spinner>
                                      </div>
                                  `
                                : this._avvAccepted
                                  ? this._seatStateTask.render({
                                        pending: () => html`
                                            <div class="padded center-aligning center-justifying horizontal layout">
                                                <ptc-ui-spinner active></ptc-ui-spinner>
                                            </div>
                                        `,
                                        complete: () => this._renderSeatManagement(seatRows, seatCounts),
                                        error: () => html`
                                            <div
                                                class="vertical double-spacing padded start-aligning layout top-margined"
                                            >
                                                <p>
                                                    <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                                                    Die Yna-Lizenzdaten konnten nicht geladen werden. Bitte prüfen Sie
                                                    Ihre Internetverbindung und versuchen Sie es erneut.
                                                </p>
                                                <button class="primary" @click=${() => this._seatStateTask.run()}>
                                                    Erneut versuchen
                                                </button>
                                            </div>
                                        `,
                                    })
                                  : this._renderAvvSection()}
                        </div>
                    </div>
                </ptc-scroller>

                <div class="save-buttons padded" ?hidden=${!this._avvAccepted || !hasPending}>
                    <button class="transparent" type="button" @click=${this._discardChanges}>Abbrechen</button>
                    <button class="primary" @click=${() => this._saveChanges(bookedSeats, nextBillingSeats)}>
                        Speichern
                    </button>
                </div>
            </div>
            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._saving}>
                <ptc-ui-spinner ?active=${this._saving}></ptc-ui-spinner>
            </div>
        `;
    }

    private _renderAvvSection() {
        return html`
            <div class="vertical double-spacing padded start-aligning layout top-margined">
                <p>
                    Bevor Sie Yna-Zugänge für Ihre Mitarbeiter verwalten können, muss der
                    <a href="https://pentacode.app/assets/Auftragsverarbeitungsvertrag_YNA.pdf" target="_blank"
                        >Auftragsverarbeitungsvertrag</a
                    >
                    akzeptiert werden.
                </p>
                <button class="primary" @click=${this._acceptAvv}>Verarbeitungsvertrag Akzeptieren</button>
            </div>
        `;
    }

    private _seatToggleColumn(licenseType: LicenseType, headerName: string, icon: IconName) {
        const col = columnDef<SeatRow>();
        return col({
            field: `seat-${licenseType}`,
            headerName,
            width: 40,
            ...verticalHeader<SeatRow>({ icon }),
            headerMenu: false,
            sortable: false,
            resizable: false,
            cellRenderer: ({ data }) => {
                const seat = this._getSeatIcon(data.id, licenseType);
                const tooltip = data.readonly ? "Änderungen nicht möglich" : seat?.tooltip;
                return html`<button
                    part="seat-toggle"
                    class="cell-fill"
                    style="color: ${seat?.color || "inherit"}"
                    ?disabled=${data.readonly}
                    @click=${() => this._toggleSeat(data.id, licenseType)}
                    ${tooltip
                        ? popover(html`<div class="small padded">${tooltip}</div>`, {
                              trigger: "hover",
                              nonInteractive: true,
                              preferAlignment: "right",
                          })
                        : nothing}
                >
                    ${seat ? html`<ptc-ui-icon name="${seat.icon}"></ptc-ui-icon>` : nothing}
                </button>`;
            },
        });
    }

    private _seatColumnDefs = (() => {
        const col = columnDef<SeatRow>();

        const employeeColumn = col({
            field: "id",
            headerName: "Mitarbeiter",
            cellRenderer: ({ data }) => html`
                <ptc-employee-cell
                    .employee=${data}
                    .positionColors=${data.positions.map((p) => app.company.getPositionColor(p))}
                    ?showStaffNumber=${true}
                    ?showResponsibilities=${true}
                ></ptc-employee-cell>
            `,
        });

        return [
            employeeColumn,
            this._seatToggleColumn(
                LicenseType.aiAssistantLite,
                LICENSE_TYPE_LABEL[LicenseType.aiAssistantLite],
                "icon.object.license-lite"
            ),
            this._seatToggleColumn(
                LicenseType.aiAssistantPro,
                LICENSE_TYPE_LABEL[LicenseType.aiAssistantPro],
                "icon.object.license-pro"
            ),
        ];
    })();

    private _renderSeatManagement(seats: SeatRow[], seatCounts: Record<LicenseType, SeatCount>) {
        const current =
            seatCounts[LicenseType.aiAssistantLite].current + seatCounts[LicenseType.aiAssistantPro].current;
        const futureCurrent =
            seatCounts[LicenseType.aiAssistantLite].futureCurrent +
            seatCounts[LicenseType.aiAssistantPro].futureCurrent;

        return html`
            <figure>
                <ptc-ui-table
                    .columnDefs=${this._seatColumnDefs}
                    .rowData=${seats}
                    .virtualized=${false}
                    .movableColumns=${false}
                    ?native=${true}
                    rowIdField="id"
                ></ptc-ui-table>
                <figcaption>
                    <ptc-ui-icon name="icon.status.access-granted" class="grey fa"></ptc-ui-icon>
                    Aktive Freigaben:
                    ${current === futureCurrent
                        ? current
                        : html`${current}
                              <ptc-ui-icon name="icon.navigation.forward" class="small subtle"></ptc-ui-icon>
                              ${futureCurrent}`}
                </figcaption>
            </figure>
        `;
    }
}
